import * as React from 'react';
import { clsx } from 'clsx';

export interface ModalProps {
  /**
   * The content of the modal (typically ModalHeader, content, and ModalFooter)
   */
  children: React.ReactNode;
  /**
   * Width of the modal
   * @default 584
   */
  width?: number | string;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

export interface ModalContentProps {
  /**
   * The content to display in the modal body
   */
  children: React.ReactNode;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

// Arbor Design System modal styles
const modalStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    backgroundColor: '#f8f8f8', // grey-050
    borderRadius: '8px',
    boxShadow: '0px 8px 24px 0px rgba(32, 32, 32, 0.12)',
    overflow: 'hidden',
    boxSizing: 'border-box' as const,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '32px 16px',
    backgroundColor: '#f8f8f8', // grey-050
    boxSizing: 'border-box' as const,
  },
  contentText: {
    flex: 1,
    fontSize: '13px',
    fontWeight: '400',
    color: '#2f2f2f',
    lineHeight: '1.5',
  },
};

/**
 * Modal component - Arbor Design System
 *
 * A modal dialog container that wraps ModalHeader, content, and ModalFooter.
 *
 * @example
 * ```tsx
 * <Modal width={584}>
 *   <ModalHeader
 *     title="Delete postal address?"
 *     onClose={() => setIsOpen(false)}
 *   />
 *   <ModalContent>
 *     <p>This will delete the postal address. Are you sure?</p>
 *   </ModalContent>
 *   <ModalFooter>
 *     <Button variant="tertiary">Cancel</Button>
 *     <Button variant="destructive-secondary">Confirm</Button>
 *   </ModalFooter>
 * </Modal>
 * ```
 */
export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  ({ children, width = 584, className, style, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('arbor-modal', className)}
        style={{
          ...modalStyles.container,
          width: typeof width === 'number' ? `${width}px` : width,
          ...style,
        }}
        role="dialog"
        aria-modal="true"
        {...props}
      >
        {children}
      </div>
    );
  }
);

Modal.displayName = 'Modal';

/**
 * ModalContent component - Arbor Design System
 *
 * The content/body area of a modal dialog.
 *
 * @example
 * ```tsx
 * <ModalContent>
 *   <p>Your content here</p>
 * </ModalContent>
 * ```
 */
export const ModalContent = React.forwardRef<HTMLDivElement, ModalContentProps>(
  ({ children, className, style, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('arbor-modal-content', className)}
        style={{ ...modalStyles.content, ...style }}
        {...props}
      >
        <div style={modalStyles.contentText}>{children}</div>
      </div>
    );
  }
);

ModalContent.displayName = 'ModalContent';
